import React, { useContext, useEffect, useState} from 'react'
import classNames from 'classnames'
import camelCase from 'lodash/camelCase'
import isEmpty from 'lodash/isEmpty'

import { canBeABundle, getPillowPairDiscount, getProductAttributesForExtend } from '@/utils/product'
import { autoScroll } from '@/utils/animations'

import { CartContext } from '@/store/cart'
import QuantityInput from './QuantityInput'
import StrikethroughVariant from './Strikethrough/StrikethroughVariant'
import ItemThumbnail from '../ItemThumbnail'
import Extend from './Extend'
import { modalActions, nonEditableSkus, MATTRESS_REMOVAL_SKUS, EXTEND_SKU } from '@/config/globals'
import { pushEditCartItem } from '@/utils/analytics'

import styles from './styles.module.scss'
import NameLink from './NameLink'
import { useProductDetailModal } from '@saatva-bits/pattern-library.components.product-detail-modal/dist/store/modal-context'

const CartItem = ({ item, sitewidePromo, setIsExtendModalVisible }) => {
    const {
        updateItemInCart,
        removeItemFromCart,
        addSavedForLater
    } = useContext(CartContext)
    const {
        setIsOpen: setDetailModalIsOpen,
        setSelectedProduct,
        setModalAction,
        setIsCartItem
    } = useProductDetailModal()
    const [productAttributes, setProductAttributes] = useState(item.productAttributes || [])
    const [isLoadingExtend, setIsLoadingExtend] = useState(false)

    const isMattress = item.productAttributes && item.magentoProductType && item.magentoProductType.toLowerCase() === 'mattresses'

    const hasPromo = canBeABundle(item)
    let addAnotherLabel = null
    const isExtendItem = item.sku === EXTEND_SKU

    if (hasPromo) {
        const pillowDiscount = getPillowPairDiscount(item)
        addAnotherLabel = pillowDiscount ? `Add a second, get $${pillowDiscount} off` : `Add a second`
    }

    const editItem = () => {
        setModalAction(modalActions.EDIT)
        setIsCartItem(true)
        setSelectedProduct(item)
        setDetailModalIsOpen(true)
        pushEditCartItem(item)
    }

    const onIncrease = async () => {
        let quantity = item.originalQuantity || item.quantity
        quantity = parseInt(quantity) + 1

        await updateItemInCart(item, quantity, 'increase')
    }

    const onDecrease = async () => {
        let quantity = item.originalQuantity || item.quantity
        quantity = parseInt(quantity) - 1

        await updateItemInCart(item, quantity, 'decrease')
    }

    const removeItem = async () => {
        let quantity = item.originalQuantity || item.quantity
        quantity = parseInt(quantity) - parseInt(item.quantity)

        if (quantity === 0) {
            await removeItemFromCart(item, null, 'cart', true, null)
        } else {
            await updateItemInCart(item, quantity, 'decrease')
        }
    }

    const saveForLater = async () => {
        await addSavedForLater(item)
        let quantity = item.originalQuantity || item.quantity
        quantity = parseInt(quantity) - parseInt(item.quantity)

        if (quantity === 0) {
            await removeItemFromCart(item, null, 'cart', true, null)
        } else {
            await updateItemInCart(item, quantity, 'decrease')
        }

        autoScroll('savedForLaterSection')
    }

    const isEditable = !nonEditableSkus.includes(item.productCode)
    const isMattressRemoval = MATTRESS_REMOVAL_SKUS.includes(item.sku)

    useEffect(() => {
        const getProductData = async () => {
            try {
                if (!item.warranty?.product) return

                const productAttributes = await getProductAttributesForExtend(item.warranty.product)
                setProductAttributes(productAttributes)
                setIsLoadingExtend(false)
            } catch (error) {
                console.error(error)
            } finally {
                setIsLoadingExtend(false)
            }
        }

        if (isExtendItem && isEmpty(productAttributes) && !isLoadingExtend) {
            setIsLoadingExtend(true)
            getProductData()
        }
    }, [productAttributes, isExtendItem, isLoadingExtend, setIsLoadingExtend])

    return (
        <div className={styles.mainContainer}>
            <ItemThumbnail cartItem={item} desktop />
            <div className={styles.contentContainer}>
                <div className={styles.cartItemTop}>
                    <NameLink item={item} />
                    {/* EXP.PERCENT_OFF_CART.CONV-321 is configured in StrikethroughVariant */}
                    <StrikethroughVariant item={item} sitewidePromo={sitewidePromo} dataSelector='cartItem' /> 
                </div>
                <div className={classNames(styles.cartItemMiddle, { [styles.groupedByDelivery]: !isExtendItem })}>
                    <ItemThumbnail cartItem={item} />
                    <ul className={styles.attributesList}>
                        {productAttributes.map(({ name, value }) => (
                            <li key={`attr-${name}`} data-selector={`cartItem_attribute_${camelCase(name)}`}>
                                <span className={styles.attributeLabel}>{name}:</span>{' '}
                                <b className={styles.attributeValue}>{value}</b>
                            </li>
                        ))}
                    </ul>
                    <div className={classNames({ [styles.groupedByDeliveryQty]: !isExtendItem })}>
                        {!isMattressRemoval && <QuantityInput
                            itemQuantity={item.quantity}
                            onIncrease={onIncrease}
                            onDecrease={onDecrease}
                            onRemove={removeItem}
                            disableIncrease={isExtendItem}
                            dataSelector='cartItem'
                        />}
                        <div className={classNames(styles.cartActions, { [styles.groupedByDeliveryActions]: !isExtendItem })}>
                            {isEditable && !isMattressRemoval && (
                                <button className={styles.editLink} onClick={editItem} data-selector="cartItem_edit">
                                    Edit
                                </button>
                            )}
                            {!isMattressRemoval && !isExtendItem &&
                                <button className={styles.saveForLaterLink} onClick={saveForLater} data-selector="cartItem_saveForLater">
                                    Save for later
                                </button>
                            }
                        </div>
                    </div>

                </div>
                <div className={styles.cartItemBottom}>
                    {hasPromo && (
                        <button className={classNames(styles.ctaButton)} onClick={onIncrease} data-selector='cartItem_addSecond'>
                            {addAnotherLabel}
                        </button>
                    )}
                    {isMattress && 
                        <Extend item={item} setIsExtendModalVisible={setIsExtendModalVisible} isMattress={isMattress} /> 
                    }
                </div>
            </div>
        </div>
    )
}

export default CartItem
